import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject, Injector, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxHttpErrorResponse, OnyxPhone } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  onyxEmailValidator,
  OnyxModalComponent,
  OnyxPhoneNumberInputComponent,
  OnyxPhonePipe,
  onyxPhoneValidator,
  OnyxTextFieldComponent,
  OnyxToastMessage,
  OnyxToastService,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { VALIDATION_ERROR_CODES } from '../../../../../../common/constants/common/validation-error-codes';
import { ConflictHelper } from '../../../../../../common/helpers/conflict.helper';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import type { ConflictField } from '../../../../../../common/interfaces/utilities/conflict-field';
import type { EmployeeRole } from '../../enums/employee-role';
import type { Employee } from '../../interfaces/employee';
import { EmployeesService } from '../../services/employees.service';
import { EmployeeRolesDropdownComponent } from '../employee-roles-dropdown/employee-roles-dropdown.component';

export type EditEmployeeModalData = Employee;

export type EditEmployeeModalForm = ReturnType<
  ReturnType<EditEmployeeModalComponent['buildForm']>['getRawValue']
>;

@Component({
  selector: 'app-add-employee',
  imports: [
    EmployeeRolesDropdownComponent,
    OnyxButtonComponent,
    OnyxTextFieldComponent,
    OnyxModalComponent,
    OnyxButtonComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxPhoneNumberInputComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './edit-employee-modal.component.html',
  styleUrl: './edit-employee-modal.component.scss',
})
export class EditEmployeeModalComponent {
  protected readonly employee = inject<EditEmployeeModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly employeesService = inject(EmployeesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly phonePipe = inject(OnyxPhonePipe);
  private readonly conflictHelper = inject(ConflictHelper);

  protected readonly I18N = 'employees.editEmployee';

  protected close$ = new Subject<void>();
  protected form = this.buildForm();
  protected loading = signal(false);

  constructor() {
    this.form.setValue({
      email: this.employee.email,
      firstName: this.employee.firstName,
      lastName: this.employee.lastName,
      phone: this.employee.phone,
      roles: this.employee.roles.map((role) => role.value),
    });
  }

  protected archiveEmployee(): void {
    this.employeesService.archiveEmployee(this.employee.uuid).subscribe(() => {
      this.toastService.showSuccess({
        message: 'employees.toasts.employeeArchived',
      });
      this.close$.next();
    });
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    this.loading.set(true);
    this.employeesService
      .editEmployee(this.employee.uuid, form)
      .subscribe({
        next: () => {
          this.toastService.showCustom(OnyxToastMessage.UPDATED_DATA);
          this.close$.next();
        },
        error: (response: OnyxHttpErrorResponse) => {
          if (!VALIDATION_ERROR_CODES.includes(response.status)) {
            throw response;
          }

          const conflictFields: ConflictField[] = [
            {
              key: 'phone',
              message: `${this.I18N}.uniqueErrors.phone`,
              params: { phone: this.phonePipe.transform(form.phone!) },
            },
          ];

          this.conflictHelper.handleConflictErrors(response, conflictFields);

          throw response;
        },
      })
      .add(() => this.loading.set(false));
  }

  protected buildForm() {
    return this.fb.group({
      firstName: this.fb.control('', [Validators.required]),
      lastName: this.fb.control('', [Validators.required]),
      phone: this.fb.control<OnyxPhone | null>(null, [
        Validators.required,
        onyxPhoneValidator,
      ]),
      email: this.fb.control({ value: '', disabled: true }, [
        Validators.required,
        onyxEmailValidator,
      ]),
      roles: this.fb.control<EmployeeRole[] | null>(null, [
        Validators.required,
      ]),
    });
  }
}
